import React from 'react';
import { connect } from 'react-redux';
import {
  Card,
  CardBody,
  CardHeader,
  CardText,
  Row,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Button,
  Alert
} from 'reactstrap';
import privateView from '../../components/hocs/privateView';
import Analytics from "../../services/Analytics";
import { SimpleLineIcon, FontAwesomeIcon } from '../../components/Icons';
import { getOptionList as getCategoryOptionList } from '../../actions/categoriesAction';
import { ModalDelete } from '../../components/Modals';
import { apiClient } from '../../utils/Api';
import ModalProductVariant from './variantGroupForm/ModalProductVariant';
import { ProductVariantItem } from './variantGroupForm/ItemList';
import AlertNotFound from '../../components/Card/AlertNotFound';

class VariantGroupCreate extends React.Component {
  constructor(props){
    super(props);
    this._isMountedFlag = false;
    this._isAttaching = false;
    this._isDetaching = false;
    this.state = {
      _form: {
        name: '',
        active: true
      },
      _errorMessage: '',
      _successMessage: '',
      _alertErrorOpen: false,
      _alertSuccessOpen: false,
      _formSubmit: false,
      _itemSubmit: false,
      isBrowseProductsOpen: false,
      listSelected: [],
      selectedItems: [],
      isRemoveConfirmationOpen: false,
      removeMode: 'all'
    };
  }

  componentDidMount(){
    this._isMountedFlag = true;
    const { email } = this.props.injectedProps;
    Analytics.recordPageView('variant_group_create', email, {
      url: window.location.href,
      location: this.props.location,
      match: this.props.match
    });
  }

  componentWillUnmount(){
    this._isMountedFlag = false;
  }

  _safeSetState = payload => {
    if(this._isMountedFlag){
      this.setState(payload);
    }
  }

  _handleInputChange = ({ target: { name, value, checked, type } }) => {
    this.setState(prevState => ({
      _form: {
        ...prevState._form,
        [name]: type === 'checkbox' ? checked : value
      }
    }));
  }

  _handleBrowseProductToggle = () => {
    if(this.state._itemSubmit){
      return;
    }
    this.setState(prevState => ({
      isBrowseProductsOpen: !prevState.isBrowseProductsOpen
    }));
  }

  _normalizeSelectedItemsFromResponse = (serverVariants = [], fallbackList = []) => {
    if(serverVariants.length === 0){
      return fallbackList;
    }
    const variantMap = {};
    fallbackList.forEach(item => {
      variantMap[item.id] = item;
    });
    serverVariants.forEach(item => {
      variantMap[item.id] = {
        ...(variantMap[item.id] || {}),
        ...item
      };
    });
    return serverVariants.map(item => variantMap[item.id] || item);
  }

  _handleAttachItems = itemIds => {
    const { id } = this.state._form;
    if(this._isAttaching || this.state._itemSubmit){
      return;
    }
    if(!id || itemIds.length === 0){
      return;
    }
    this._isAttaching = true;
    const variantIds = [...new Set(itemIds)];
    this._safeSetState({
      _itemSubmit: true
    });
    return apiClient().post(`/admins/variant-groups/${id}/variants`, {
      variant_ids: variantIds
    })
    .then(({ data }) => {
      const serverVariants = Array.isArray(data?.data?.variants) ? data.data.variants : [];
      const fallbackList = this.state.selectedItems;
      this._safeSetState({
        selectedItems: this._normalizeSelectedItemsFromResponse(serverVariants, fallbackList),
        _alertErrorOpen: false
      });
      return true;
    })
    .catch(err => {
      const message = err?.response?.data?.message || 'Failed to attach items.';
      this._safeSetState({
        _alertErrorOpen: true,
        _alertSuccessOpen: false,
        _errorMessage: message
      });
      return false;
    })
    .finally(() => {
      this._isAttaching = false;
      this._safeSetState({
        _itemSubmit: false
      });
    });
  }

  _toggleRemoveConfirmation = () => {
    this.setState(prevState => ({
      isRemoveConfirmationOpen: !prevState.isRemoveConfirmationOpen
    }));
  }

  _handleOpenRemoveConfirmation = e => {
    e.preventDefault();
    if(this.state._itemSubmit){
      return;
    }
    const { listSelected, selectedItems } = this.state;
    if(selectedItems.length === 0){
      return;
    }
    this.setState({
      isRemoveConfirmationOpen: true,
      removeMode: listSelected.length > 0 ? 'selected' : 'all'
    });
  }

  _handleDetachItems = () => {
    if(this._isDetaching || this.state._itemSubmit){
      return;
    }
    const { listSelected, selectedItems, removeMode, _form: { id } } = this.state;
    const variantIds = removeMode === 'all' ? selectedItems.map(item => item.id) : listSelected;
    if(variantIds.length === 0){
      this._safeSetState({
        isRemoveConfirmationOpen: false
      });
      return;
    }
    this._isDetaching = true;
    this._safeSetState({
      _itemSubmit: true
    });
    apiClient().delete(`/admins/variant-groups/${id}/variants`, {
      data: {
        variant_ids: variantIds
      }
    })
    .then(({ data }) => {
      const serverVariants = Array.isArray(data?.data?.variants) ? data.data.variants : [];
      const fallbackList = selectedItems.filter(item => !variantIds.includes(item.id));
      this._safeSetState({
        selectedItems: this._normalizeSelectedItemsFromResponse(serverVariants, fallbackList),
        listSelected: [],
        isRemoveConfirmationOpen: false,
        _alertErrorOpen: false
      });
    })
    .catch(err => {
      const message = err?.response?.data?.message || 'Failed to remove items.';
      this._safeSetState({
        _alertErrorOpen: true,
        _alertSuccessOpen: false,
        _errorMessage: message,
        isRemoveConfirmationOpen: false
      });
    })
    .finally(() => {
      this._isDetaching = false;
      this._safeSetState({
        _itemSubmit: false
      });
    });
  }

  _getRemoveConfirmationBody = () => {
    const { removeMode, listSelected } = this.state;
    if(removeMode === 'selected'){
      return `Are you sure to remove ${listSelected.length} selected item(s)?`;
    }
    return 'Are you sure to remove all items?';
  }

  _handleSubmit = e => {
    e.preventDefault();
    const { name, active, id } = this.state._form;
    const normalizedName = (name || '').trim();
    if(id){
      return;
    }
    if(!normalizedName){
      this._safeSetState({
        _alertErrorOpen: true,
        _alertSuccessOpen: false,
        _errorMessage: 'Name is required.'
      });
      return;
    }
    this._safeSetState({
      _formSubmit: true,
      _alertErrorOpen: false,
      _alertSuccessOpen: false
    });
    apiClient().post('/admins/variant-groups', {
      name: normalizedName,
      active
    })
    .then(({ data }) => {
      const detail = data.data || {};
      this._safeSetState(prevState => ({
        _form: {
          ...prevState._form,
          id: detail.id,
          name: detail.name || prevState._form.name,
          active: typeof detail.active === 'boolean' ? detail.active : prevState._form.active
        },
        _formSubmit: false,
        _alertErrorOpen: false,
        _alertSuccessOpen: true,
        _successMessage: data.message || 'Variant group created successfully.'
      }));
      if(!this.props.dataCategories.length){
        this.props.getCategories();
      }
    })
    .catch(err => {
      const message = err?.response?.data?.message || 'Failed to create variant group.';
      this._safeSetState({
        _formSubmit: false,
        _alertErrorOpen: true,
        _alertSuccessOpen: false,
        _errorMessage: message
      });
    });
  }

  render(){
    const { dataCategories } = this.props;
    const { selectedItems, listSelected, _form } = this.state;
    const isCreated = Boolean(_form.id);
    return(
      <div className="animated fadeIn">
        {isCreated ? (
          <ModalProductVariant
            isOpen={this.state.isBrowseProductsOpen}
            toggle={this._handleBrowseProductToggle}
            dataCategories={dataCategories}
            selectedItems={selectedItems}
            onSubmit={this._handleAttachItems}
            isSubmitting={this.state._itemSubmit}
          />
        ) : null}
        <ModalDelete
          isOpen={this.state.isRemoveConfirmationOpen}
          toggle={this._toggleRemoveConfirmation}
          onDelete={this._handleDetachItems}
          modalBody={this._getRemoveConfirmationBody()}
        />
        <Row>
          <Col xs={12} sm={6} md={6}>
            <Card>
              <CardHeader>
                <i className="fa fa-align-justify"></i> Create New Product Variant Group
              </CardHeader>
              <CardBody>
                <Alert
                  color="danger"
                  isOpen={this.state._alertErrorOpen}
                  toggle={() => this.setState({_alertErrorOpen: false})}
                >
                  {this.state._errorMessage}
                </Alert>
                <Alert
                  color="success"
                  isOpen={this.state._alertSuccessOpen}
                  toggle={() => this.setState({_alertSuccessOpen: false})}
                >
                  {this.state._successMessage}
                </Alert>
                <Form onSubmit={this._handleSubmit}>
                  <FormGroup>
                    <Label for="variant_group_name">Name</Label>
                    <Input
                      id="variant_group_name"
                      type="text"
                      name="name"
                      placeholder="Enter group name"
                      value={_form.name}
                      disabled={isCreated}
                      onChange={this._handleInputChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label for="variant_group_active">Set to Active &nbsp;</Label>
                    <label className="switch switch-text switch-success-outline-alt">
                      <Input
                        id="variant_group_active"
                        type="checkbox"
                        className="switch-input"
                        name="active"
                        value="true"
                        checked={_form.active}
                        disabled={isCreated}
                        onChange={this._handleInputChange}
                      />
                      <span className="switch-label" data-on="Yes" data-off="No"></span>
                      <span className="switch-handle"></span>
                    </label>
                  </FormGroup>
                  <div className="mt-3">
                    <Button color="primary" disabled={this.state._formSubmit || isCreated}>
                      {this.state._formSubmit ? 'Creating...' : 'Create Group'}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
          {isCreated ? (
            <Col xs={12} md={6}>
              <Card>
                <CardHeader>
                  <FontAwesomeIcon iconType="align-justify" /> Select Item
                </CardHeader>
                <CardBody>
                  <CardText>Add / remove product variants.</CardText>
                  <div className="mb-1" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <button
                        type="button"
                        className="btn btn-link link text-primary mr-1 p-0"
                        onClick={this._handleBrowseProductToggle}
                        disabled={this.state._itemSubmit}
                      >
                        <SimpleLineIcon iconType="plus" className="mr-1" />
                        Browse
                      </button>
                      <button
                        type="button"
                        className="btn btn-link link text-danger mr-2 p-0"
                        onClick={this._handleOpenRemoveConfirmation}
                        disabled={this.state._itemSubmit}
                      >
                        <SimpleLineIcon iconType="trash" className="mr-1" />
                        <span>
                          Remove {listSelected.length ? `(${listSelected.length})` : 'All'}
                        </span>
                      </button>
                    </div>
                    <div>
                      {listSelected.length > 0 ? (
                        <Button
                          outline
                          color="danger"
                          size="sm"
                          type="button"
                          className="mr-1"
                          style={{ fontSize: '.5rem' }}
                          onClick={() => this.setState({ listSelected: [] })}
                          disabled={this.state._itemSubmit}
                        >
                          Clear Selected
                        </Button>
                      ) : null}
                    </div>
                  </div>
                  <div style={{maxHeight: 480, overflow: 'auto', borderTop: '1px solid #ddd', paddingTop: '.5rem'}}>
                    {selectedItems.length === 0 ? (
                      <AlertNotFound message="No Record Found!" />
                    ) : selectedItems.map(item => (
                      <ProductVariantItem
                        key={item.id}
                        data={item}
                        checked={listSelected.includes(item.id)}
                        onSelectedChange={({ target: { checked } }) => {
                          this.setState(prevState => ({
                            listSelected: checked ?
                              [...prevState.listSelected, item.id] :
                              prevState.listSelected.filter(id => id !== item.id)
                          }));
                        }}
                      />
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col>
          ) : null}
        </Row>
      </div>
    )
  }
}

const mapStateToProps = ({ auth: { isFetch, roles, isLogged, id, email }, masterCategories }) => ({
  guardData: { isFetch, roles, isLogged, id, email },
  dataCategories: masterCategories.options
});

const mapDispatchToProps = dispatch => ({
  getCategories: () => dispatch(getCategoryOptionList())
});

const enhance = connect(mapStateToProps, mapDispatchToProps);

export default enhance(privateView(VariantGroupCreate, ['superadministrator', 'administrator', 'inventory_admin']));
